import type { FC } from 'react';

import { Placeholder, useRendering, useSetting } from '@eneco/cms';
import { Divider, Grid, Stack } from '@eneco/sparky';

import { CENTERED_SLOTS, resolveSlot } from './slots';

/**
 * `Centered` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Carries no content of its own — it is a width constraint around whatever sits in its placeholder,
 * plus an optional trailing divider. Both settings are Sitecore rendering parameters, which the
 * CMS schema compiles into ordinary fields, so `useSetting` reads them from either provider.
 *
 * `colSpan` is `'6'` or `'8'` (`libs/sitecore/types/manual/Centered.tsx`); anything else falls back to
 * 8, the wider and more forgiving of the two. The `columns`/`gridColumn` breakpoints mirror the legacy
 * division (`libs/sitecore/components/content/src/Centered/Centered.tsx`) exactly: an explicit column
 * count is required for `Grid.Item`'s negative line indices (`-2`, `-3`, …) to resolve against, and the
 * column narrows further at `lg`/`xl` rather than settling at its `md` width.
 */
const Centered: FC = () => {
  const rendering = useRendering();
  const colSpan = useSetting('colSpan') === '6' ? 6 : 8;
  const hasDivider = useSetting('hasDivider') === 'true';

  const mainSlot = resolveSlot(rendering, CENTERED_SLOTS.main);
  if (!mainSlot) return null;

  return (
    <Grid columns={{ initial: '8', md: '12' }}>
      <Grid.Item gridColumn={colSpan === 8 ? { initial: '1/-1', lg: '2/-2', xl: '3/-3' } : { initial: '1/-1', lg: '3/-3', xl: '4/-4' }}>
        <Stack gap="12">
          <Placeholder name={mainSlot} />
          {hasDivider && <Divider />}
        </Stack>
      </Grid.Item>
    </Grid>
  );
};

export default Centered;
